import type { RadioGroupItemProps } from '@radix-ui/react-radio-group';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { ForwardedRef, forwardRef } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { Icon } from '../../icon';
import { VisuallyHidden } from '../../visually_hidden';
import { theme as commonTheme } from '../common_input.theme';
import { useInputRatingContext } from './_input_rating.context';
import { theme } from './input_rating.theme';

const elements = {
	item: styled(RadioGroup.Item)`
		--background: none;
		--radio-border-color: ${theme.getValue('indicatorBorderColor')};
		--radio-background: ${theme.getValue('indicatorBackground')};
		--radio-outline: none;
		--indicator-background: ${theme.getValue('indicatorBorderColor')};

		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		box-sizing: border-box;
		display: flex;
		gap: ${theme.getValue('itemGap')};
		align-items: center;
		cursor: default;
		font: ${theme.getValue('itemFont')};
		color: ${theme.getValue('itemTextColor')};
		background: var(--background);
		border-radius: ${coreTokens.borderRadius.xSmall};
		transition:
			border 150ms ease-out,
			background 150ms ease-out,
			box-shadow 50ms ease-out;
		position: relative;

		&:before {
			opacity: 0;
			scale: 0.9;
			transition:
				opacity 200ms ease,
				scale 200ms ease;
		}

		&[data-state='checked'] {
			font: ${theme.getValue('checkedItemFont')};

			--radio-border-color: ${theme.getValue('checkedIndicatorBorderColor')};
			--indicator-background: ${theme.getValue('checkedIndicatorBorderColor')};
		}

		&:focus-visible {
			outline: 2px solid ${theme.getValue('focusColor')};
			outline-offset: 2px;
		}

		&[data-variant='none'] {
			&:hover {
				--radio-border-color: ${theme.getValue('checkedIndicatorBorderColor')};

				&:before {
					content: '';
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
					opacity: 1;
					scale: 1;
					width: calc(100% + ${coreTokens.size.x0_5});
					height: calc(100% + ${coreTokens.size.x0_5});
					background: ${theme.getValue('indicatorHover')};
					z-index: -1;
					border-radius: ${coreTokens.borderRadius.circle};
				}
			}

			&:active {
				--radio-outline: 0 0 0 2px ${theme.getValue('activeIndicatorOutlineColor')};
			}

			&[data-invalid='true'] {
				--radio-border-color: ${commonTheme.getValue('invalidBorderColor')};
			}
		}

		&[data-variant='outlined'] {
			padding: ${theme.getValue('outlinedItemPadding')};
			border: 1px solid ${theme.getValue('outlinedItemBorderColor')};
			min-height: ${theme.getValue('outlinedItemMinHeight')};
			border-radius: ${theme.getValue('outlinedItemBorderRadius')};

			&:hover {
				border-color: ${theme.getValue('hoverOutlinedItemBorderColor')};
			}

			&:active {
				box-shadow: 0 0 0 2px ${theme.getValue('activeOutlinedItemOutlineColor')};
			}

			&[data-state='checked'] {
				--background: ${theme.getValue('checkedOutlinedItemBackground')};
			}

			&[data-invalid='true'] {
				border-color: ${commonTheme.getValue('invalidBorderColor')};
			}
		}

		// Disabled/ReadOnly
		&[data-read-only='true'],
		&[data-disabled='true'] {
			--radio-border-color: ${theme.getValue('disabledIndicatorBorderColor')};
			--radio-background: ${theme.getValue('disabledIndicatorBackground')};
			--indicator-background: ${theme.getValue('disabledIndicatorBorderColor')};
			opacity: 0.7;

			&:hover,
			&:active {
				--radio-border-color: ${theme.getValue('disabledIndicatorBorderColor')};
				--radio-outline: none;
			}

			&[data-variant='outlined'] {
				--background: ${theme.getValue('disabledOutlinedItemBackground')};
				border: none;

				&:hover,
				&:active {
					box-shadow: none;
				}
			}
		}
	`,
	radio: styled.span`
		display: flex;
		width: ${theme.getValue('indicatorOuterSize')};
		height: ${theme.getValue('indicatorOuterSize')};
		border-radius: 50%;
		align-items: center;
		justify-content: center;
		flex: 0 0 auto;
	`,
};

export interface InputRatingItemProps extends RadioGroupItemProps {
	label: string;
	filled: boolean;
}

export const CellulaInputRatingItem = forwardRef(function CellulaInputRatingItem(
	{ label, filled, ...rest }: InputRatingItemProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const context = useInputRatingContext();

	return (
		<elements.item
			data-variant={context.variant}
			data-invalid={context.invalid ?? undefined}
			aria-readonly={context.readOnly ?? undefined}
			data-read-only={context.readOnly ?? undefined}
			data-disabled={context.disabled ?? undefined}
			{...rest}
			ref={ref}
		>
			<elements.radio>{filled ? <Icon type='starFilled' size='xLarge' /> : <Icon type='star' size='xLarge' />}</elements.radio>
			<VisuallyHidden>
				<span data-text={label}>{label}</span>
			</VisuallyHidden>
		</elements.item>
	);
});
